// PRD #02 — Address validation (no dependencies)
// Lightweight validators for EVM/Solana/TRON/BTC addresses

export type Chain = 'evm' | 'sol' | 'tron' | 'btc' | 'stellar';

export function isHex(s: string) { 
  return /^0x[0-9a-fA-F]+$/.test(s); 
}

export function isBase58(s: string) { 
  return /^[1-9A-HJ-NP-Za-km-z]+$/.test(s); 
}

export function validateAddress(chain: Chain, addr: string): boolean {
  switch (chain) {
    case 'evm':
      if (!/^0x[0-9a-fA-F]{40}$/.test(addr)) return false;
      return isValidEIP55(addr);
    case 'sol':
      // Solana addresses: length 32-44, base58
      return isBase58(addr) && addr.length >= 32 && addr.length <= 44;
    case 'stellar':
      // Stellar addresses: G... format, base58, 56 characters
      // Also accepts memo (up to 28 characters, base64 or numeric)
      return /^G[1-9A-HJ-NP-Za-km-z]{55}$/.test(addr) || 
             /^[A-Z0-9]{1,28}$/.test(addr); // Memo format
    case 'tron':
      // TRON addresses: base58check, T... format
      return /^T[1-9A-HJ-NP-Za-km-z]{33}$/.test(addr);
    case 'btc':
      // BTC: legacy P2PKH/P2SH or bech32
      return /^[13][a-km-zA-HJ-NP-Z1-9]{25,34}$/.test(addr) || 
             /^bc1[0-9a-z]{11,71}$/.test(addr);
    default:
      return false;
  }
}

function isValidEIP55(addr: string): boolean {
  // Reasonable compromise for FE: accept if it's:
  //  - all lowercase 0x[0-9a-f]{40}, or  
  //  - all uppercase 0x[0-9A-F]{40}, or
  //  - mixed case [0-9a-fA-F]{40} (assume valid on FE)
  // Strict EIP-55 verification happens on Edge Function
  if (!isHex(addr)) return false;
  const body = addr.slice(2);
  const isLower = /^[0-9a-f]{40}$/.test(body);
  const isUpper = /^[0-9A-F]{40}$/.test(body);
  return isLower || isUpper || /^[0-9a-fA-F]{40}$/.test(body);
}

// Normalize address format
export function normalizeAddress(chain: Chain, addr: string): string {
  switch (chain) {
    case 'evm':
      return addr.toLowerCase(); // Normalize to lowercase for storage
    case 'sol':
    case 'tron':
    case 'btc':
      return addr; // Keep as-is for base58 chains
    default:
      return addr;
  }
}

// Get address format description for UI
export function getAddressFormat(chain: Chain): string {
  switch (chain) {
    case 'evm':
      return 'Ethereum address (0x...)';
    case 'sol':
      return 'Solana address (base58, 32-44 chars)';
    case 'stellar':
      return 'Stellar address (G...) or memo';
    case 'tron':
      return 'TRON address (T...)';
    case 'btc':
      return 'Bitcoin address (1.../3.../bc1...)';
    default:
      return 'Valid address for selected chain';
  }
}